import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import searchIcon from "./../../media/search-icon.svg";
import Logger from "../../utils/Logger";
import { LoadingComponent } from "../../App.js";
import httpClient from "../../services/httpClient";

const InPost = () => {
  const { t } = useTranslation();
  const [trackingId, setTrackingId] = useState("");
  const [trackingData, setTrackingData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!trackingId.trim()) {
      setError(t("logistics.enterTrackingNumber"));
      setTrackingData(null);
      setHasSearched(false);
      return;
    }

    setLoading(true);
    setError(null);
    setTrackingData(null);
    setHasSearched(true);

    try {
      Logger.log(`Śledzenie przesyłki InPost: ${trackingId}`);
      const response = await httpClient.get(`/InPost/tracking/${trackingId}`, {
        withAuth: true,
      });

      setTrackingData(response.data);
      setError(null);
      Logger.log("Dane śledzenia InPost otrzymane:", response.data);
    } catch (error) {
      Logger.error("Błąd podczas śledzenia przesyłki InPost:", error);
      if (error.response?.status === 404) {
        setError(t("logistics.noParcelFound"));
      } else if (error.response?.status === 400) {
        setError(t("logistics.invalidTrackingNumber"));
      } else {
        setError(t("logistics.errorTracking"));
      }
      setTrackingData(null);
    } finally {
      setLoading(false);
    }
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString("pl-PL", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getStatusColor = (status) => {
    const statusLower = status.toLowerCase();
    if (statusLower.includes("dostarczona")) return "green";
    if (
      statusLower.includes("umieszczona") ||
      statusLower.includes("automacie")
    )
      return "#8B4513"; // brązowy
    if (statusLower.includes("doręczenia") || statusLower.includes("trasie"))
      return "blue";
    if (statusLower.includes("przyjęta") || statusLower.includes("oddziale"))
      return "purple";
    if (statusLower.includes("odebrana") || statusLower.includes("nadawcy"))
      return "teal";
    if (statusLower.includes("przygotowana") || statusLower.includes("nadana"))
      return "gray";
    return "black";
  };

  const safeRenderValue = (value, fallback = "Brak informacji") => {
    if (typeof value === "string" && value.trim() !== "") {
      return value;
    } else if (typeof value === "object" && value !== null) {
      if (value.description) {
        return value.description;
      }
      return JSON.stringify(value);
    }
    return fallback;
  };

  return (
    <div className="inpost-service-checker">
      <h1>{t("logistics.inpostTracking")}</h1>

      <form className="formularz" onSubmit={handleSubmit}>
        <label htmlFor="trackingId">
          <div className="input-container">
            <input
              type="text"
              name="trackingId"
              id="trackingId"
              placeholder={t("logistics.trackingNumber")}
              value={trackingId}
              onChange={(e) => setTrackingId(e.target.value)}
            />
            <img
              src={searchIcon}
              alt="search Icon"
              className="search-icon"
              onClick={handleSubmit}
            />
          </div>
        </label>
        <div className="check">
          <input
            type="submit"
            value={loading ? t("logistics.checking") : t("logistics.track")}
            disabled={loading}
          />
        </div>
      </form>

      {error && <div className="error">{error}</div>}

      {loading && hasSearched && <LoadingComponent />}

      {trackingData && !loading && (
        <div className="tracking-info">
          <h2>
            Informacje o przesyłce <b>{trackingData.trackingNumber}</b>
          </h2>

          <div className="package-info">
            <p>
              <strong>Status:</strong> {safeRenderValue(trackingData.status)}
            </p>
            <p>
              <strong>Usługa:</strong> {safeRenderValue(trackingData.service)}
            </p>
            <p>
              <strong>Typ:</strong> {safeRenderValue(trackingData.type)}
            </p>
            {trackingData.customAttributes?.size && (
              <p>
                <strong>Rozmiar:</strong> {trackingData.customAttributes.size}
              </p>
            )}
            {trackingData.customAttributes?.targetMachineId && (
              <p>
                <strong>Docelowy paczkomat:</strong>{" "}
                {trackingData.customAttributes.targetMachineId}
              </p>
            )}
            {trackingData.customAttributes?.dropoffMachineId && (
              <p>
                <strong>Paczkomat nadania:</strong>{" "}
                {trackingData.customAttributes.dropoffMachineId}
              </p>
            )}
          </div>

          {trackingData.customAttributes?.targetMachineDetail && (
            <div className="machine-info">
              <h3>Informacje o docelowym paczkomacie:</h3>
              <p>
                <strong>Nazwa:</strong>{" "}
                {trackingData.customAttributes.targetMachineDetail.name}
              </p>
              <p>
                <strong>Adres:</strong>{" "}
                {
                  trackingData.customAttributes.targetMachineDetail.address
                    ?.line1
                }
                {trackingData.customAttributes.targetMachineDetail.address
                  ?.line2 &&
                  `, ${trackingData.customAttributes.targetMachineDetail.address.line2}`}
              </p>
              <p>
                <strong>Godziny otwarcia:</strong>{" "}
                {trackingData.customAttributes.targetMachineDetail.openingHours}
              </p>
              {trackingData.customAttributes.targetMachineDetail
                .locationDescription && (
                <p>
                  <strong>Opis lokalizacji:</strong>{" "}
                  {
                    trackingData.customAttributes.targetMachineDetail
                      .locationDescription
                  }
                </p>
              )}
            </div>
          )}

          <h3>Historia przesyłki:</h3>
          <div className="tracking-timeline">
            {trackingData.trackingDetails &&
            trackingData.trackingDetails.length > 0 ? (
              trackingData.trackingDetails.map((event, index) => (
                <div key={index} className="tracking-event">
                  <div className="event-content">
                    <div className="event-header">
                      <span
                        className="event-status"
                        style={{ color: getStatusColor(event.status) }}
                      >
                        {event.status}
                      </span>
                      <span className="event-timestamp">
                        {formatTimestamp(event.datetime)}
                      </span>
                    </div>
                    {event.originStatus && (
                      <div className="event-description">
                        Kod statusu: {event.originStatus}
                      </div>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <p>Brak danych o wydarzeniach dla tej przesyłki.</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default InPost;
